<template>
  <Wrap>
    <h2 class="title">
      <IconBase
        class="fire-img"
        name="local-fire-department"
      />
      Популярные статьи
    </h2>

    <MosaicListView
      class="mosaic"
      :items="articles"
      :more-button="hasMoreArticles"
      more-button-text="Показать ещё"
      more-data-test="destination-articles-btn-read-more"
      :loading="loading"
      no-last-item-margin
      :number-of-skeleton-cards="pageSize"
      @load-more="fetchData"
    >
      <template #default="{ item, mode, position }">
        <AppLink
          class="article-card-wrapper"
          :to="`/journal/articles/${item.slug}/`"
        >
          <Card
            v-article-view-tracker="getAnalyticsParams(item, position)"
            v-article-click-tracker="getAnalyticsParams(item, position)"
            :is-reader-experience="item.isUgc"
            :item="item"
            :mode="mode"
            :locale="ru"
          />
        </AppLink>
      </template>
      <template #skeleton="{ mode }">
        <Skeleton
          :mode="mode"
          :loading="loading"
        />
      </template>
    </MosaicListView>
  </Wrap>
</template>

<script lang="ts" setup>
  import { type JournalContentItem, Card, Skeleton } from '@experience/mosaic-list-article-card-package';
  import { MosaicListView } from '@experience/mosaic-list-view-package';
  import { ru } from 'date-fns/locale';
  import { storeToRefs } from 'pinia';
  import { onBeforeMount, onBeforeUnmount, ref } from 'vue';

  import { useClickTracker, useViewTracker } from '@/services/analytics';
  import { useJournalContent } from '@/entities/journalContent';
  import type { JournalArticleClickParams, JournalArticleViewParams } from '@/shared/api/sogu';
  import { IconBase } from '@/shared/ui/iconBase';
  import Wrap from '@/shared/Wrap.vue';

  const store = useJournalContent();
  const { getPopularItemsList, reset } = store;
  const { articles, loading, hasNext: hasMoreArticles } = storeToRefs(store);

  const pageSize = ref(15);

  const vArticleViewTracker = useViewTracker('journal:article.view');
  const vArticleClickTracker = useClickTracker('journal:article.click');

  const getAnalyticsParams = (
    item: JournalContentItem,
    position: number,
  ): JournalArticleClickParams['params'] | JournalArticleViewParams['params'] => ({
    article_id: item.id,
    block: 'recent',
    position,
  });

  const fetchData = async () => getPopularItemsList({ page_size: pageSize.value });

  onBeforeMount(async () => {
    if (!articles.value.length) {
      await fetchData();
    }
  });

  onBeforeUnmount(() => reset());
</script>

<style lang="scss" scoped>
  .title {
    display: flex;
    gap: 0 var(--alias-space-8);
    align-items: center;
    margin-top: 80px;
    color: var(--alias-color-text-default);
    font-weight: 800;
    font-size: 40px;
    line-height: 48px;

    @include tablet {
      gap: 0 var(--alias-space-4);
      margin-top: 40px;
      font-size: 24px;
      line-height: 32px;
    }

    @include mobile {
      margin-bottom: 0;
    }
  }

  .icon {
    fill: var(--alias-color-icon-default);
  }

  .mosaic {
    margin-top: var(--alias-space-32);

    @include mobile {
      margin-top: var(--alias-space-16);
    }
  }

  .fire-img {
    @include tablet {
      transform: scale(0.75);
    }
  }

  .article-card-wrapper {
    color: var(--alias-color-text-default);
    text-decoration: none;

    &:focus,
    &:visited {
      color: var(--alias-color-text-default);
      text-decoration: none;
    }
  }
</style>
